import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import JsonLd from '@/components/base/JsonLd';
import usePageMeta from '@/hooks/usePageMeta';
import { articles, categories } from '@/mocks/news';

const PAGE_SIZE = 4;

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') || 'all';
  const [activeCat, setActiveCat] = useState(initialCat);
  const [keyword, setKeyword] = useState(searchParams.get('q') || '');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setActiveCat(searchParams.get('cat') || 'all');
    setKeyword(searchParams.get('q') || '');
    setPage(1);
  }, [searchParams]);

  usePageMeta({
    title: '话题合集 - 看料大瓜｜分类浏览热门事件与补瓜清单',
    description:
      '看料大瓜话题合集页，按综艺热度、剧集观察、音乐舞台、网络热梗与造型红毯等分类浏览全部话题，支持关键词搜索，快速定位你想补的瓜。',
    keywords: '看料大瓜合集,看料大瓜盘点,话题分类,吃瓜补课,热门事件汇总',
    path: '/topics',
    ogType: 'website',
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((item) => {
      const matchCat = activeCat === 'all' || item.category === activeCat;
      const matchKw =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCat, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentList = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const popular = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);

  const selectCat = (catId: string) => {
    const next = new URLSearchParams(searchParams);
    if (catId === 'all') next.delete('cat');
    else next.set('cat', catId);
    if (keyword.trim()) next.set('q', keyword.trim());
    else next.delete('q');
    setSearchParams(next);
    setPage(1);
  };

  const onSearch = (value: string) => {
    setKeyword(value);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set('q', value.trim());
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '话题合集 - 看料大瓜',
      description: '按分类浏览看料大瓜整理的全部娱乐热点话题合集。',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kanliaodagua.example/' },
        { '@type': 'ListItem', position: 2, name: '话题合集', item: 'https://kanliaodagua.example/topics' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: filtered.slice(0, 10).map((item, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: item.title,
        url: `https://kanliaodagua.example/article/${item.id}`,
      })),
    },
  ];

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <JsonLd data={structuredData} />
      <SiteHeader />

      <div className="border-b border-background-200 bg-background-100 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 pb-10 md:px-6 md:pb-12">
          <Breadcrumb items={[{ label: '话题合集' }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">话题合集</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这里收录了本站整理的全部热点话题。按分类筛选，或直接搜索关键词，找到你想补的那一条。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => selectCat('all')}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors ${
                    activeCat === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  全部话题
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => selectCat(cat.id)}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors ${
                      activeCat === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="relative">
                <i
                  className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"
                  aria-hidden="true"
                ></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder="搜索话题标题、标签或关键词"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                />
              </div>

              <p className="text-xs text-foreground-500">
                共找到 <strong className="text-foreground-900">{filtered.length}</strong> 条话题
                {activeCat !== 'all' && (
                  <>
                    {' · '}分类：
                    {categories.find((c) => c.id === activeCat)?.name}
                  </>
                )}
              </p>
            </div>

            <div className="mt-6 flex flex-col gap-5">
              {currentList.length > 0 ? (
                currentList.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="horizontal" />
                ))
              ) : (
                <div className="rounded-lg border border-dashed border-background-400 bg-background-100 py-20 text-center">
                  <i className="ri-search-eye-line text-3xl text-foreground-400" aria-hidden="true"></i>
                  <p className="mt-3 text-sm text-foreground-600">没有匹配的话题，换个关键词或者切换分类试试。</p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <nav aria-label="分页导航" className="mt-10 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setPage(num)}
                    className={`h-9 w-9 rounded-md text-sm transition-colors ${
                      page === num
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-300 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  下一页
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                </button>
              </nav>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">站内热门</h2>
              <div className="mt-3">
                {popular.map((item, idx) => (
                  <ArticleCard key={item.id} article={item} variant="compact" index={idx} />
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">分类快捷入口</h2>
              <ul className="mt-3 space-y-2">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <button
                      type="button"
                      onClick={() => selectCat(cat.id)}
                      className="flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs text-foreground-500">{cat.count} 个</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 rounded-lg bg-secondary-900 p-5 text-secondary-100">
              <h2 className="font-heading text-base font-bold text-background-50">没找到想看的？</h2>
              <p className="mt-2 text-sm leading-relaxed text-secondary-200">
                告诉我们你关注的方向，我们会优先整理相关话题。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                联系我们
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}